import React, { useContext, useEffect } from 'react';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { BasicButton } from '../../../components/common/Button';
import { InputWord } from '../../../components/common/Input';
import { ToastQueueRefContext } from '../../../components/common/Toast';
import FormHeader from '../../../components/forms/FormHeader';
import { connexionType } from '../../../schema/LoginSchema';
import AuthService from '../../../services/auth.service';
import UsersService from '../../../services/users.service';

type LoginFormProps = {
  setPageStep: React.Dispatch<React.SetStateAction<string>>;
  setSkemeetUserInfos: React.Dispatch<
    React.SetStateAction<{ temporaryAccessToken: string; firstname: string; lastname: string; cellphone: string }>
  >;
  connexionMethods: UseFormReturn<{ nickNameOrEmail: string; password: string }, undefined>;
  setIsDoubleAuthEmail: (isDoubleAuthEmail: boolean) => void;
  setRedirectUrl: (redirectUrl: string | undefined) => void;
  redirect: (check2FA?: boolean, redirectUrl?: string) => void;
};

const LoginForm = ({
  setPageStep,
  setSkemeetUserInfos,
  connexionMethods,
  setIsDoubleAuthEmail,
  redirect,
  setRedirectUrl,
}: LoginFormProps) => {
  const navigate: NavigateFunction = useNavigate();
  const searchQueryParams = useSearchParams()[0];
  const toastQueueRef = useContext(ToastQueueRefContext);
  const authService = new AuthService();
  const usersService = new UsersService();

  const loginWithForm = async ({ nickNameOrEmail, password }: connexionType) => {
    const loginReturn = await authService.login({ nickNameOrEmail, password }, false);

    if (loginReturn.changeSkemeetPassword) {
      setSkemeetUserInfos({
        temporaryAccessToken: loginReturn.temporaryAccessToken ? loginReturn.temporaryAccessToken : '',
        firstname: loginReturn.firstname ? loginReturn.firstname : '',
        lastname: loginReturn.lastname ? loginReturn.lastname : '',
        cellphone: loginReturn.cellphone ? loginReturn.cellphone : '',
      });

      setPageStep('updateSkemeetPassword');
    } else if (loginReturn.oneTimePasswordActivated) {
      setPageStep('connexionStepTwo');
      setIsDoubleAuthEmail(false);
      setRedirectUrl(loginReturn.redirect);
    } else if (loginReturn.doubleAuthEmailActivated) {
      setPageStep('connexionStepTwo');
      setIsDoubleAuthEmail(true);
      setRedirectUrl(loginReturn.redirect);
    } else {
      redirect(true, loginReturn.redirect);
    }
  };

  const validateAccount = async (mailActivatedToken: string) => {
    await usersService.validateAccount(mailActivatedToken);

    toastQueueRef?.current?.addToast({
      timer: 10_000,
      description: 'Votre compte a été validé avec succès. Vous pouvez maintenant vous connecter.',
      type: 'SUCCESS',
    });
  };

  const mergeAccount = async (mergeAccountToken: string) => {
    const result = await authService.fcPscMergeAccount(mergeAccountToken);

    toastQueueRef?.current?.addToast({
      timer: 10_000,
      description: result.alreadyMerged
        ? 'Ce compte a déjà été associé. Vous pouvez vous connecter.'
        : 'Votre compte a été associé avec succès. Vous pouvez maintenant vous connecter.',
      type: 'SUCCESS',
    });
  };

  useEffect(() => {
    const mailActivatedToken = searchQueryParams.get('mailActivatedToken');
    if (mailActivatedToken) {
      validateAccount(mailActivatedToken);
    }

    const mergeAccountToken = searchQueryParams.get('mergeAccountToken');
    if (mergeAccountToken) {
      mergeAccount(mergeAccountToken);
      navigate(`/login`, { replace: true });
    }
  }, []);

  return (
    <>
      <FormHeader
        topLabel="Vous n'avez pas de compte ?"
        actionTopLabel='Inscrivez-vous ici'
        actionTopOnClick={() => navigate(`/subscription`)}
        title='Heureux de vous revoir !'
        addAppsConnect={true}
      />

      <div className='mt-4 mb-2 text-[1rem] font-semibold'>Avec une adresse mail</div>

      <FormProvider {...connexionMethods}>
        <form onSubmit={connexionMethods.handleSubmit(loginWithForm)}>
          <Controller
            name='nickNameOrEmail'
            control={connexionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Adresse mail ou identifiant'
                  type='text'
                  placeholder='Adresse mail ou identifiant'
                  actionLabel={
                    <span
                      className='text-green-secondary cursor-pointer font-medium hover:font-semibold hover:underline'
                      onClick={() => navigate(`/reset-nickname`)}
                    >
                      Identifiant oublié ?
                    </span>
                  }
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                  // errorMessage={<>L'identifiant ne correspond à aucun compte. <span className="font-bold">Veuillez <span className="underline cursor-pointer" onClick={() => setPageStep(pageStep.includes('Participant') ? 'subscriptionParticipant' : 'subscription')}>créer un compte</span></span>.</>}
                />
              </>
            )}
          />
          <Controller
            name='password'
            control={connexionMethods.control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <InputWord
                  label='Mot de passe'
                  type='password'
                  placeholder='Mot de passe'
                  actionLabel={
                    <span
                      className='text-green-secondary cursor-pointer font-medium hover:font-semibold hover:underline'
                      onClick={() => navigate(`/reset-password`)}
                    >
                      Mot de passe oublié ?
                    </span>
                  }
                  value={value}
                  onChangeValue={onChange}
                  errorMessage={error?.message}
                  autoComplete='off'
                />
              </>
            )}
          />
          <BasicButton label='Se connecter' fullWidth={true} isSubmitButton={true} />
        </form>
      </FormProvider>
    </>
  );
};

export default LoginForm;
